(() => {
  if (window.icAuraWishlistBound) return;
  window.icAuraWishlistBound = true;
  const KEY = 'shopify-wishlist';
  const CACHE_KEY = 'ic-aura-wishlist-cache-v1';
  const parse = (value, fallback) => { try { return JSON.parse(value); } catch { return fallback; } };
  const config = parse(document.querySelector('[data-ic-wishlist-config]')?.dataset.icWishlistConfig, {}) || {};
  const labels = {
    save: 'Save to wishlist', saved: 'Saved to wishlist', added: 'Added to your wishlist.', removed: 'Removed from your wishlist.',
    remove: 'Remove', removeProduct: 'Remove __PRODUCT__ from wishlist', viewProduct: 'View product', soldOut: 'Sold out',
    unavailable: 'This product is no longer available.', loading: 'Loading saved items…',
    storageError: 'Your browser could not save this change. Allow site storage and try again.',
    loadError: 'Some current product details could not be loaded. Open the product page for the latest price and availability.',
    countLabel: 'Wishlist: __COUNT__ saved items',
    ...(config.labels || {})
  };
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(value);
  const root = typeof config.root === 'string' && /^\/(?!\/)/.test(config.root) ? config.root : '/';
  const products = new Map();
  const attempted = new Set();
  let state = { handles: [], cache: {}, blocked: false };
  let refreshing = false;
  let fetchStopped = false;
  let loadFailed = false;
  let feedbackTimer;

  function localProductUrl(value, handle) {
    try {
      const url = new URL(value, location.origin);
      if (url.origin === location.origin && url.pathname.endsWith('/products/' + handle)) return url.pathname + url.search;
    } catch { /* Invalid stored links fall back to the native product route. */ }
    return root + 'products/' + encodeURIComponent(handle);
  }
  function imageUrl(value) {
    if (typeof value !== 'string' || !value) return '';
    try {
      const url = new URL(value, location.origin);
      if (url.protocol === 'https:' && (url.origin === location.origin || url.hostname === 'cdn.shopify.com')) return url.href;
    } catch { /* Never inject an untrusted stored URL. */ }
    return '';
  }
  function productData(value, fresh = false) {
    if (!value || !validHandle(value.handle)) return null;
    const result = {
      handle: value.handle,
      title: typeof value.title === 'string' ? value.title.slice(0,1000) : '',
      url: localProductUrl(value.url, value.handle),
      image: imageUrl(value.image)
    };
    if (fresh) {
      result.priceText = typeof value.priceText === 'string' ? value.priceText.slice(0,100) : '';
      result.available = value.available === true;
      result.unavailable = value.unavailable === true;
    }
    return result;
  }
  function readState() {
    try {
      const stored = parse(localStorage.getItem(KEY), []);
      const handles = Array.isArray(stored) ? [...new Set(stored.filter(validHandle))] : [];
      const rawCache = parse(localStorage.getItem(CACHE_KEY), {});
      const cache = {};
      if (rawCache && typeof rawCache === 'object' && !Array.isArray(rawCache)) {
        handles.forEach(handle => { const data = productData(rawCache[handle]); if (data) cache[handle] = data; });
      }
      return { handles, cache, blocked: false };
    } catch { return { handles: [], cache: {}, blocked: true }; }
  }
  function writeState(handles, cache) {
    try {
      // Preserve the original handles key. Cached titles/images contain no stale prices.
      const previousCache = localStorage.getItem(CACHE_KEY);
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      try { localStorage.setItem(KEY, JSON.stringify(handles)); }
      catch (error) {
        try { previousCache === null ? localStorage.removeItem(CACHE_KEY) : localStorage.setItem(CACHE_KEY, previousCache); } catch { /* Storage can become unavailable between writes. */ }
        throw error;
      }
      state = { handles, cache, blocked: false };
      return true;
    } catch { announce(labels.storageError); return false; }
  }
  function announce(message) {
    if (!message) return;
    let feedback = document.querySelector('[data-ic-wishlist-feedback]');
    if (!feedback) {
      feedback = document.createElement('p');
      feedback.className = 'ic-wishlist-feedback';
      feedback.dataset.icWishlistFeedback = '';
      feedback.setAttribute('role', 'status');
      feedback.setAttribute('aria-live', 'polite');
      document.body.append(feedback);
    }
    feedback.textContent = message;
    clearTimeout(feedbackTimer);
    feedbackTimer = setTimeout(() => { feedback.textContent = ''; }, 6000);
  }
  function ingest(scope = document) {
    const nodes = [...scope.querySelectorAll('[data-ic-wishlist-product]')];
    if (scope.matches?.('[data-ic-wishlist-product]')) nodes.push(scope);
    nodes.forEach(node => {
      const data = productData(parse(node.dataset.icWishlistProduct, null), true);
      if (data) products.set(data.handle, data);
    });
  }
  function syncButtons() {
    document.querySelectorAll('[data-ic-wishlist-toggle]').forEach(button => {
      const saved = state.handles.includes(button.dataset.icWishlistToggle);
      button.hidden = false;
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', saved ? button.dataset.icRemoveLabel : button.dataset.icSaveLabel);
      const label = button.querySelector('[data-ic-wishlist-label]');
      if (label) label.textContent = saved ? labels.saved : labels.save;
    });
    document.querySelectorAll('[data-ic-wishlist-count]').forEach(count => {
      count.textContent = String(state.handles.length);
      count.hidden = !state.handles.length;
    });
    document.querySelectorAll('[data-ic-wishlist-link]').forEach(link => {
      if (labels.countLabel) link.setAttribute('aria-label', labels.countLabel.replace('__COUNT__', String(state.handles.length)));
    });
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }
  function renderPage() {
    const page = document.querySelector('[data-ic-wishlist-page]');
    if (!page) return;
    const grid = page.querySelector('[data-ic-wishlist-grid]');
    const fragment = document.createDocumentFragment();
    state.handles.forEach(handle => {
      const fresh = products.get(handle);
      const data = fresh || state.cache[handle] || { handle, title: loadFailed ? labels.viewProduct : labels.loading, url: localProductUrl('', handle), image: '' };
      const item = element('article', 'ic-wishlist-item');
      item.dataset.icWishlistItem = handle;
      const media = element(data.unavailable ? 'div' : 'a', 'ic-wishlist-item-media');
      if (!data.unavailable) { media.href = data.url; media.setAttribute('aria-label', data.title); }
      if (data.image) {
        const img = element('img'); img.src = data.image; img.alt = data.title; img.width = 360; img.height = 360; img.loading = 'lazy'; media.append(img);
      }
      item.append(media);
      const heading = element('h2');
      const title = element(data.unavailable ? 'span' : 'a', '', data.title || labels.unavailable);
      if (!data.unavailable) title.href = data.url;
      heading.append(title); item.append(heading);
      if (fresh?.priceText) item.append(element('p', 'ic-wishlist-item-price', fresh.priceText));
      if (fresh?.unavailable || (fresh && !fresh.available)) item.append(element('p', 'ic-wishlist-item-availability', fresh.unavailable ? labels.unavailable : labels.soldOut));
      const actions = element('div', 'ic-wishlist-item-actions');
      if (!data.unavailable) { const view = element('a', '', labels.viewProduct); view.href = data.url; actions.append(view); }
      const remove = element('button', '', labels.remove); remove.type = 'button'; remove.dataset.icWishlistRemove = handle;
      remove.setAttribute('aria-label', (labels.removeProduct || labels.remove || '').replace('__PRODUCT__', data.title || handle));
      actions.append(remove); item.append(actions); fragment.append(item);
    });
    grid.replaceChildren(fragment);
    page.querySelector('[data-ic-wishlist-empty]').hidden = state.handles.length > 0 || state.blocked;
    const status = page.querySelector('[data-ic-wishlist-page-status]');
    status.textContent = state.blocked ? labels.storageError : loadFailed ? labels.loadError : state.handles.some(handle => !products.has(handle)) ? labels.loading : '';
    page.setAttribute('aria-busy', String(refreshing));
  }
  async function refreshMissingProducts() {
    if (!document.querySelector('[data-ic-wishlist-page]') || refreshing || fetchStopped || state.blocked) return;
    const missing = state.handles.filter(handle => !products.has(handle) && !attempted.has(handle));
    if (!missing.length) return;
    refreshing = true; renderPage();
    try {
      for (const handle of missing) {
        if (!state.handles.includes(handle)) continue;
        attempted.add(handle);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        try {
          const response = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal });
          if (response.status === 404) {
            products.set(handle, { ...(state.cache[handle] || {}), handle, title: state.cache[handle]?.title || labels.unavailable, unavailable: true });
            continue;
          }
          if (!response.ok) throw new Error('Product unavailable: ' + response.status);
          const product = await response.json();
          if (product.handle !== handle || !Array.isArray(product.variants)) throw new Error('Invalid product data');
          const variant = product.variants.find(item => item.available) || product.variants[0];
          if (!variant || !Number.isFinite(variant.price)) throw new Error('Missing product variant');
          const priceText = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: config.currency || 'USD', currencyDisplay: 'narrowSymbol' }).format(variant.price / 100);
          const data = productData({ handle, title: product.title, url: root + 'products/' + handle + '?variant=' + variant.id, image: product.featured_image?.src || product.featured_image, priceText, available: variant.available }, true);
          if (data) products.set(handle, data);
        } catch {
          // Stop on verification, rate limits and network failures. No automatic retry.
          loadFailed = true; fetchStopped = true; break;
        } finally { clearTimeout(timeout); }
      }
    } finally { refreshing = false; renderPage(); }
  }
  function refresh() { ingest(); syncButtons(); renderPage(); refreshMissingProducts(); }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-ic-wishlist-toggle], [data-ic-wishlist-remove]');
    if (!button) return;
    event.preventDefault();
    const handle = button.dataset.icWishlistToggle || button.dataset.icWishlistRemove;
    if (!validHandle(handle)) return;
    state = readState();
    if (state.blocked) { announce(labels.storageError); return; }
    const removing = state.handles.includes(handle);
    const handles = removing ? state.handles.filter(value => value !== handle) : [...state.handles, handle];
    const cache = { ...state.cache };
    const data = productData(products.get(handle));
    if (removing) delete cache[handle]; else if (data) cache[handle] = data;
    if (!writeState(handles, cache)) return;
    syncButtons(); renderPage(); announce(removing ? labels.removed : labels.added);
    if (button.hasAttribute('data-ic-wishlist-remove')) {
      (document.querySelector('[data-ic-wishlist-remove]') || document.getElementById('IcWishlistHeading'))?.focus();
    }
  });
  window.addEventListener('storage', event => {
    if (event.key === KEY || event.key === CACHE_KEY || event.key === null) { state = readState(); refresh(); }
  });
  document.addEventListener('ic:products:updated', refresh);
  document.addEventListener('shopify:section:load', refresh);
  state = readState();
  refresh();
})();
